const MONTH_KEY = /^(\d{4})-(0[1-9]|1[0-2])$/;
const DATE_KEY = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

function monthParts(monthKey: string): [number, number] {
  const match = MONTH_KEY.exec(monthKey);
  if (!match) throw new Error("Invalid calendar month");
  return [Number(match[1]), Number(match[2])];
}

export function monthKeyInTimezone(date: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "2-digit",
    timeZone: timezone,
  }).formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  if (!year || !month) throw new Error("Could not determine calendar month");
  return `${year}-${month}`;
}

export function initialBookingMonth(
  requestedMonth: string | null,
  timezone: string,
  now = new Date(),
): string {
  return requestedMonth && MONTH_KEY.test(requestedMonth)
    ? requestedMonth
    : monthKeyInTimezone(now, timezone);
}

export function shiftMonthKey(monthKey: string, delta: number): string {
  const [year, month] = monthParts(monthKey);
  const shifted = new Date(Date.UTC(year, month - 1 + delta, 1));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, "0")}`;
}

export function calendarCells(monthKey: string): Array<number | null> {
  const [year, month] = monthParts(monthKey);
  const firstWeekday = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
  const dayCount = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const mondayOffset = (firstWeekday + 6) % 7;
  return [
    ...Array.from({ length: mondayOffset }, () => null),
    ...Array.from({ length: dayCount }, (_, index) => index + 1),
  ];
}

export function formatMonthHeading(monthKey: string): string {
  const [year, month] = monthParts(monthKey);
  return new Intl.DateTimeFormat("en", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, 1, 12)));
}

export function formatDateHeading(dateKey: string): string {
  const match = DATE_KEY.exec(dateKey);
  if (!match) return "";
  return new Intl.DateTimeFormat("en", {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(
    new Date(
      Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12),
    ),
  );
}

export function formatBookingTime(
  startValue: string,
  endValue: string,
  timezone: string,
): string {
  const start = new Date(startValue);
  const end = new Date(endValue);
  const day = new Intl.DateTimeFormat("en", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: timezone,
  }).format(start);
  const time = new Intl.DateTimeFormat("en", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezone,
  });
  return `${time.format(start)} – ${time.format(end)}, ${day}`;
}
